import type { StateCreator } from 'zustand';

import {
  addReissuedMedicationToPrescriptionPad,
  advancePrescriptionPadStep,
  reloadPrescriptionAssitant,
  resetPrescriptionHistory,
  resetPrescriptionPad,
  updateDevicePadItems,
  updateMedicationPadItems,
  updatePrescriptionHistoryItemsToAction,
  updatePrescriptionHistoryItemsToCancel,
  updatePrescriptionHistoryItemsToReissue,
  updatePrescriptionHistoryPreviousItems,
  updatePrescriptionPadDispenseNote,
  updatePrescriptionPadReviewAction,
  updatePrescriptionPadStep,
  updateShowCancellation
} from './actions';
import { removeMedicationPadItemById } from './actions/removeMedicationPadItemById';
import { updateMedicationPadItemById } from './actions/updateMedicationPadItemById';
import { initialPrescriptionHistoryState, initialPrescriptionPadState } from './constants';
import { isPrescriptionInProgress, isPrescriptionPadInSync, isValidPrescriptionPad } from './selectors';
import type { PrescriptionAssistantSliceType } from './types';

export const prescriptionAssistantSlice: StateCreator<
  PrescriptionAssistantSliceType,
  [['zustand/immer', never], ['zustand/devtools', never]],
  [],
  PrescriptionAssistantSliceType
> = (set, get, store) => ({
  // State
  prescriptionAssistant: {
    prescriptionHistory: initialPrescriptionHistoryState,
    prescriptionPad: initialPrescriptionPadState
  },
  // Selectors/Computed State
  isValidPrescriptionPad: () => isValidPrescriptionPad(get),
  isPrescriptionPadInSync: () => isPrescriptionPadInSync(get),
  isPrescriptionInProgress: () => isPrescriptionInProgress(get),

  // Actions
  updateMedicationPadItems: (medicationPadItems) => updateMedicationPadItems(set, get, store)(medicationPadItems),
  removeMedicationPadItemById: (medicationPadItemId) =>
    removeMedicationPadItemById(set, get, store)(medicationPadItemId),
  updateMedicationPadItemById: (updatedMedicationPadItem, { ffPatPrescriptionDosageMinLengthValidation }) =>
    updateMedicationPadItemById(
      set,
      get,
      store
    )(updatedMedicationPadItem, {
      ffPatPrescriptionDosageMinLengthValidation
    }),
  addReissuedMedicationToPrescriptionPad: (availableMedications, reissuedMedication, flags) =>
    addReissuedMedicationToPrescriptionPad(set, get, store)(availableMedications, reissuedMedication, flags),
  updateDevicePadItems: (devicePadItems) => updateDevicePadItems(set, get, store)(devicePadItems),
  updatePrescriptionPadReviewAction: (action) => updatePrescriptionPadReviewAction(set, get, store)(action),
  updatePrescriptionPadStep: (step) => updatePrescriptionPadStep(set, get, store)(step),
  updatePrescriptionHistoryItemsToReissue: (itemsToReissue) =>
    updatePrescriptionHistoryItemsToReissue(set, get, store)(itemsToReissue),
  updatePrescriptionHistoryItemsToCancel: (itemsToCancel) =>
    updatePrescriptionHistoryItemsToCancel(set, get, store)(itemsToCancel),
  updatePrescriptionHistoryItemsToAction: (itemsToAction) =>
    updatePrescriptionHistoryItemsToAction(set, get, store)(itemsToAction),
  updatePrescriptionHistoryPreviousItems: (previousItems) =>
    updatePrescriptionHistoryPreviousItems(set, get, store)(previousItems),
  updateShowCancellation: (show) => updateShowCancellation(set, get, store)(show),
  updatePrescriptionPadDispenseNote: (dispenseNote) => updatePrescriptionPadDispenseNote(set, get, store)(dispenseNote),
  resetPrescriptionPad: () => resetPrescriptionPad(set, get, store)(),
  advancePrescriptionPadStep: () => advancePrescriptionPadStep(set, get, store)(),
  resetPrescriptionHistory: () => resetPrescriptionHistory(set, get, store)(),
  reloadPrescriptionAssitant: () => reloadPrescriptionAssitant(set, get, store)()
});
